import { useState } from 'react';
import type { Venue } from '@peles/shared';
import { useCreateVenue } from '../../../hooks/useVenues';
import {
  GoogleAddressAutocomplete,
  type AddressFields,
} from '../../common/GoogleAddressAutocomplete';
import { extractApiErrorMessage } from '../../../utils/apiError';

export interface QuickVenueFormProps {
  /** Called with the venue once it is saved. */
  onCreated: (venue: Venue) => void;
  onCancel: () => void;
}

/**
 * Just enough of a venue to pick it for an event — its name and address —
 * without leaving the form being filled in. The rest (rooms, contacts,
 * capacity) is on the venue's own page.
 */
export function QuickVenueForm({ onCreated, onCancel }: QuickVenueFormProps) {
  const createVenue = useCreateVenue();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  // The chosen suggestion, with the text it filled in. Once the text is
  // edited it no longer describes that place, so its city/state/ZIP are dropped.
  const [picked, setPicked] = useState<{ text: string; fields: AddressFields } | null>(null);
  const place = picked && picked.text === address ? picked.fields : null;
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      const venue = await createVenue.mutateAsync({
        name: name.trim(),
        address: address.trim() || undefined,
        city: place?.city || undefined,
        state: place?.state || undefined,
        zipCode: place?.zipCode || undefined,
        country: place?.country || undefined,
      });
      onCreated(venue);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not save the venue'));
    }
  };

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New venue</h3>
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <div>
        <label htmlFor="quick-venue-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          id="quick-venue-name"
          type="text"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Boca Raton Resort"
          className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="quick-venue-address"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Address <span className="text-xs text-gray-400 font-normal">(optional)</span>
        </label>
        <GoogleAddressAutocomplete
          id="quick-venue-address"
          value={address}
          onChange={setAddress}
          onAddressSelect={(fields) =>
            setPicked({ text: fields.address || fields.formattedAddress || '', fields })
          }
          placeholder="Start typing an address"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!name.trim() || createVenue.isPending}
          className="h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {createVenue.isPending ? 'Saving…' : 'Save venue'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
